import React, { useState, useContext, createContext, PropsWithChildren, useMemo, useRef } from 'react';
import useRouter from 'vl-core/useRouter';
import { ErrorModal } from 'vl-common/src/components/Modals/CustomModals/Error';
import { SuccessModal } from 'vl-common/src/components/Modals';

import { AddNote } from 'vl-common/src/components/Appointments/AddNoteModal';
import { AppointmentTypeModal } from 'vl-common/src/components/Appointments/AppointmentTypeModal';
import { CancelAppointmentModal } from 'vl-common/src/components/Appointments/CancelAppointmentModal';
import { RescheduleReasonModal } from 'vl-common/src/components/Appointments/RescheduleReasonModal';
import { ViewNotesModal } from 'vl-common/src/components/Appointments/ViewNotesModal';
import AddF2FReferralModal from 'vl-common/src/components/Appointments/AddF2FReferralModal';
import { Appointment } from '../../schemas/shared';
import { useAuth, useSystemType } from '../../context/provider.app';
import useApi from '../../hooks/useApi';
import { getCurrentLocation } from '../getCurrentLocation';
import { useBookingFlowControl } from '../../hooks/useBookingFlowControl';
import useAdminPageURL from '../../hooks/useAdminPageURL';

type AppointmentManagementContextType = {
  actionedAppointment: Appointment | null;
  onSetActionedAppointment: (params: { appointment: Appointment; refetchFn?: () => void }) => void;
  openAddNotesModal: () => void;
  openViewNotesModal: () => void;
  openCancelAppointmentModal: () => void;
  openCallTypeChangeModal: () => void;
  openRescheduleAppointmentModal: () => void;
  openAddF2FReferralModal: () => void;
};

const AppointmentManagementContext = createContext<AppointmentManagementContextType | null>(null);

const AppointmentManagementProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [auth] = useAuth();
  const { Api, toasted } = useApi();
  const [systemType] = useSystemType();
  const router = useRouter();

  const [actionedAppointment, setActionedAppointment] = useState<Appointment | null>(null);

  const [showAddNote, setShowAddNote] = useState(false);
  const [showViewNotes, setShowViewNotes] = useState(false);
  const [showCancelAppointmentModal, setShowCancelAppointmentModal] = useState(false);
  const [showCallTypeChangeModal, setShowCallTypeChangeModal] = useState(false);
  const [showRescheduleAppointmentModal, setShowRescheduleAppointmentModal] = useState(false);
  const [showAddF2FReferralModal, setShowAddF2FReferralModal] = useState(false);

  const [confirmCallTypeChangeModal, setConfirmCallTypeChangeModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cancelAppointmentError, setCancelAppointmentError] = useState(false);
  const [rescheduleError, setRescheduleError] = useState(false);

  const { initializeRescheduleBooking } = useBookingFlowControl();

  const { getAppointmentBookingPageURL } = useAdminPageURL();

  const onAppointmentUpdateHandler = useRef<(() => void) | null>(null);

  const withResetActionedAppointment = (fn: () => void) => () => {
    fn();
    setActionedAppointment(null);
  };

  const openAddNotesModal = () => setShowAddNote(true);
  const openViewNotesModal = () => setShowViewNotes(true);
  const openCancelAppointmentModal = () => setShowCancelAppointmentModal(true);
  const openCallTypeChangeModal = () => setShowCallTypeChangeModal(true);
  const openRescheduleAppointmentModal = () => setShowRescheduleAppointmentModal(true);
  const openAddF2FReferralModal = () => setShowAddF2FReferralModal(true);

  const closeAddNotesModal = withResetActionedAppointment(() => setShowAddNote(false));
  const closeViewNotesModal = withResetActionedAppointment(() => setShowViewNotes(false));
  const closeCancelAppointmentModal = withResetActionedAppointment(() => setShowCancelAppointmentModal(false));
  const closeCallTypeChangeModal = withResetActionedAppointment(() => setShowCallTypeChangeModal(false));
  const closeRescheduleAppointmentModal = withResetActionedAppointment(() => setShowRescheduleAppointmentModal(false));
  const closeAddF2FReferralModal = withResetActionedAppointment(() => setShowAddF2FReferralModal(false));

  const onChangeCallType = async (callType: 'AUDIO' | 'VIDEO') => {
    try {
      if (callType !== actionedAppointment?.call_type_code) {
        await Api.admin.changeAppointmentCallType(auth.user.user_guid, callType, actionedAppointment?.appointment_id);
        onAppointmentUpdateHandler?.current?.();
      }
    } catch (e) {
      // @ts-expect-error
      setErrorMessage(e?.response?.data?.message);
    }
    closeCallTypeChangeModal();
    setConfirmCallTypeChangeModal(true);
  };

  const onCancelAppointment = async (data: { key: string }) => {
    const { key } = data;
    try {
      await Api.booking.appointmentCancel(actionedAppointment?.appointment_id, key, systemType);
      onAppointmentUpdateHandler?.current?.();
    } catch (e) {
      // @ts-expect-error
      setErrorMessage(e?.response?.data?.message);
      setCancelAppointmentError(true);
    }
    closeCancelAppointmentModal();
  };

  const onAppointmentReschedule = async (data: { key: string }) => {
    const { key } = data;
    const params = {
      pat_guid: actionedAppointment?.patient_user_guid
    };
    if (!actionedAppointment) {
      setErrorMessage('Missing appointment details');
      setRescheduleError(true);
      return;
    }

    try {
      const res = await Api.referrer.search(params);
      const { users } = res as any;
      if (!users || users.length === 0) {
        setErrorMessage('Patient not found. Cannot reschedule appointment.');
        setRescheduleError(true);
        return;
      }
      const { pathname, search } = getCurrentLocation();

      const currentPageLocation = `${pathname}${search}`;
      const bookingPageURL = getAppointmentBookingPageURL();

      initializeRescheduleBooking({
        patientId: actionedAppointment.patient_user_guid,
        caseId: actionedAppointment.case_id,
        apptType: actionedAppointment.appt_type_code,
        previousApptId: actionedAppointment.appointment_id,
        rescheduleReason: key,
        redirectRoute: currentPageLocation
      });

      router.push(bookingPageURL);
    } catch (error) {
      toasted(error);
      throw error;
    }
  };

  const onSetActionedAppointment = ({
    appointment,
    refetchFn
  }: {
    appointment: Appointment;
    refetchFn?: () => void;
  }) => {
    setActionedAppointment(appointment);

    if (refetchFn) {
      onAppointmentUpdateHandler.current = refetchFn;
    }
  };

  const contextValue = useMemo(() => {
    return {
      actionedAppointment,
      onSetActionedAppointment,
      openAddNotesModal,
      openViewNotesModal,
      openCancelAppointmentModal,
      openCallTypeChangeModal,
      openRescheduleAppointmentModal,
      openAddF2FReferralModal
    };
  }, [actionedAppointment]);

  return (
    <AppointmentManagementContext.Provider value={contextValue}>
      {children}

      {actionedAppointment && (
        <>
          <AddNote
            show={showAddNote}
            data={actionedAppointment}
            close={closeAddNotesModal}
            onCreated={closeAddNotesModal}
            isAddMode
          />

          <ViewNotesModal
            show={showViewNotes}
            data={actionedAppointment}
            close={closeViewNotesModal}
            getNotesParams={{
              case_id: actionedAppointment?.case_id
            }}
          />

          <AppointmentTypeModal
            isOpen={showCallTypeChangeModal}
            toggle={closeCallTypeChangeModal}
            cancelBtnHandler={closeCallTypeChangeModal}
            confirmBtnHandler={onChangeCallType}
            // @ts-expect-error
            selected_AP_Type={actionedAppointment?.call_type_code}
            isNewCasePage={false}
          />

          <CancelAppointmentModal
            isOpen={showCancelAppointmentModal}
            toggle={closeCancelAppointmentModal}
            yesBtnHandler={onCancelAppointment}
            noBtnHandler={closeCancelAppointmentModal}
            systemType={systemType}
          />

          <RescheduleReasonModal
            isOpen={showRescheduleAppointmentModal}
            toggle={closeRescheduleAppointmentModal}
            noBtnHandler={closeRescheduleAppointmentModal}
            yesBtnHandler={onAppointmentReschedule}
            systemType={systemType}
          />

          <AddF2FReferralModal
            open={showAddF2FReferralModal}
            onCancel={closeAddF2FReferralModal}
            onCreated={closeAddF2FReferralModal}
            appointment={actionedAppointment}
            patient_user_guid={actionedAppointment?.patient_user_guid}
          />
        </>
      )}

      <SuccessModal
        title="Your appointment’s call type has been changed successfully"
        open={confirmCallTypeChangeModal}
        onCancel={() => setConfirmCallTypeChangeModal(false)}
        cancelText="Ok"
        data-testid="return-to-appointments"
      />

      <ErrorModal
        open={cancelAppointmentError}
        title="Failed to cancel appointment"
        subTitle={errorMessage}
        onCancel={() => setCancelAppointmentError(false)}
      />

      <ErrorModal
        open={rescheduleError}
        title="Failed to reschedule appointment"
        subTitle={errorMessage}
        onCancel={() => setRescheduleError(false)}
      />
    </AppointmentManagementContext.Provider>
  );
};

export const useManageAppointmentContext = () => {
  const ctx = useContext(AppointmentManagementContext);

  if (!ctx) {
    throw new Error('useManageAppointmentContext must be used inside <AppointmentManagementProvider>');
  }

  return ctx;
};

export default AppointmentManagementProvider;
